import React, { useEffect, useState } from 'react';
import { assistantAPI } from '../../services/assistant';
import '../../styles/Assistant.css';

const describeError = (err) => err?.response?.data?.error || err?.message || 'Could not save the models';
const price = (p) => (p ? `$${p.input} input, $${p.cachedInput} cached, $${p.output} output per million tokens` : 'Price not known');

/**
 * Company admins: which models and efforts people may choose in the chat
 * window, and which are chosen until they change them.
 */
export default function AssistantModels({ companyId, status, onSaved }) {
  const current = status.models || { models: [], efforts: [] };
  const catalogue = status.catalogue || { models: [], efforts: [] };
  const [models, setModels] = useState([]);
  const [efforts, setEfforts] = useState([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [defaultEffort, setDefaultEffort] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setModels(current.models.map((m) => m.id));
    setEfforts(current.efforts.map((e) => e.id));
    setDefaultModel(current.defaultModel || '');
    setDefaultEffort(current.defaultEffort || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const pickedModels = catalogue.models.filter((m) => models.includes(m.id));
  const pickedEfforts = catalogue.efforts.filter((e) => efforts.includes(e.id));
  const modelDefault = models.includes(defaultModel) ? defaultModel : pickedModels[0]?.id || '';
  const effortDefault = efforts.includes(defaultEffort) ? defaultEffort : pickedEfforts[0]?.id || '';
  const changed = JSON.stringify([[...models].sort(), [...efforts].sort(), modelDefault, effortDefault])
    !== JSON.stringify([current.models.map((m) => m.id).sort(), current.efforts.map((e) => e.id).sort(), current.defaultModel || '', current.defaultEffort || '']);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await assistantAPI.setModels(companyId, { models, efforts, defaultModel: modelDefault || null, defaultEffort: effortDefault || null });
      setError('');
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!catalogue.models.length) return null;
  return (
    <div className="ai-models">
      <h4>Models people can choose</h4>
      <p className="settings-help">
        People choose a model and effort in the AI assistant&apos;s chat window, from those allowed here. More capable models and higher effort give more careful answers, but are slower and cost more.
        {!current.chosenByAdmin && ' Until you choose, only the server’s model and effort are available.'}
      </p>
      <fieldset className="ai-models-group">
        <legend>Models</legend>
        {catalogue.models.map((m) => (
          <label key={m.id} className="settings-toggle">
            <input type="checkbox" checked={models.includes(m.id)} disabled={saving} onChange={() => toggle(models, setModels, m.id)} />
            <span>
              <strong>{m.label}</strong>
              <small>{m.description} {price(m.pricesPerMillionTokens)}.</small>
            </span>
          </label>
        ))}
      </fieldset>
      <fieldset className="ai-models-group">
        <legend>Effort</legend>
        {catalogue.efforts.map((e) => (
          <label key={e.id} className="settings-toggle">
            <input type="checkbox" checked={efforts.includes(e.id)} disabled={saving} onChange={() => toggle(efforts, setEfforts, e.id)} />
            <span><strong>{e.label}</strong><small>{e.description}</small></span>
          </label>
        ))}
      </fieldset>
      <div className="ai-models-defaults">
        <label>
          Default model
          <select value={modelDefault} disabled={saving || !pickedModels.length} onChange={(e) => setDefaultModel(e.target.value)}>
            {pickedModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
        <label>
          Default effort
          <select value={effortDefault} disabled={saving || !pickedEfforts.length} onChange={(e) => setDefaultEffort(e.target.value)}>
            {pickedEfforts.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
        <button type="button" className="btn-primary" disabled={saving || !changed || !models.length || !efforts.length} onClick={save}>
          {saving ? 'Saving…' : 'Save models'}
        </button>
      </div>
      {(!models.length || !efforts.length) && <p className="settings-help">Allow at least one model and one effort.</p>}
      {saved && !changed && <p className="settings-help" role="status">Saved.</p>}
      {error && <div className="error-message" role="alert">{error}</div>}
    </div>
  );
}
